import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useStore } from "../../zustand/store";
import svg from "../../components/svg";
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    hitApi,
    setMyState,
    Pagination,
    skeletonLoader,
    NoDataFoundDataTable,
    commonToast,
    validations,
    dateFormatter,
    getNameInitials,
    assetsUrlPath,
} from "../../components/commonAction";
import Select from "react-select";
import "../../styles/admin-table.css";

// ─── Filter option constants ──────────────────────────────────────────────────
const optionsAdmin = [
    { value: '', label: 'Select Role' },
    { value: 'user', label: 'User' },
];
const statusOption = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' },
];
const verifiedOption = [
    { value: 'verified', label: 'Verified' },
    { value: 'NotVerified', label: 'Not Verified' },
];

// ─── Component ────────────────────────────────────────────────────────────────
const Users = ({ pageTitle }) => {
    const store = useStore((state) => state);

    const [state, setState] = useState({
        data: [],
        limit: 10,
        page: 1,
        keyword: '',
        sort: 'createdAt=-1',
        role: '',
        status: 3,
        verication_status: '',
        totalRecords: '',
        userLoading: false,
        clearKeyword: false,
    });

    const [user, setUser] = useState({
        name: '',
        email: '',
        password: '',
        role: '',
        target: '',
        confirmPass: '',
        status: '',
        isEdit: false,
    });

    const [btnLoading, setBtnLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const firstInputRef = useRef(null);

    // ─── Data fetching ────────────────────────────────────────────────────────
    useEffect(() => {
        getUserList();
    }, [state.page, state.status, state.role, state.clearKeyword, state.verication_status]);

    const getUserList = () => {
        setMyState(setState, { ...state, userLoading: true });
        const { limit, page, keyword, sort, role, status, verication_status } = state;
        const data = { limit, page: keyword ? 1 : page, keyword, sort, role };

        if (status !== 3) {
            data['status'] = status;
        }
        if (verication_status) {
            data['verication_status'] = verication_status;
        }

        hitApi({ url: 'user/user-list', method: 'GET', data }, (resp) => {
            if (resp.status) {
                const response = resp.data.records;
                setMyState(setState, {
                    ...state,
                    data: response,
                    userLoading: false,
                    totalRecords: resp.data.totalRecords,
                });
            }
        });
    };

    const clearSearch = () => {
        setMyState(setState, { ...state, clearKeyword: true, keyword: '' });
    };

    // ─── Modal handlers ───────────────────────────────────────────────────────
    const handleClose = () => {
        setShowModal(false);
        setMyState(setUser, {
            name: '',
            email: '',
            password: '',
            role: '',
            target: '',
            isEdit: false,
            confirmPass: '',
        });
    };

    const handleShow = (item) => {
        setShowModal(true);
        if (item) {
            setMyState(setUser, {
                name: item.name,
                email: item.email,
                password: '',
                role: item.role,
                target: item._id,
                isEdit: true,
                status: item.status,
            });
        }
    };

    // Auto-focus first input when modal opens
    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
    }, [showModal]);

    // ─── Create / update handlers ─────────────────────────────────────────────
    const emptyUserState = () => {
        setMyState(setUser, {
            name: '', email: '', password: '', role: '', target: '', status: '', isEdit: false,
        });
    };

    const createUser = (e) => {
        e.preventDefault();
        if (user.name.trim() && user.email && user.password && user.role) {
            const isValid =
                validations(user.email, 'email') && validations(user.password, 'password');
            if (isValid) {
                setBtnLoading(true);
                hitApi(
                    { url: 'user/add-user', method: 'POST', data: { ...user } },
                    (resp) => {
                        setBtnLoading(false);
                        if (resp.status) {
                            handleClose();
                            getUserList();
                            emptyUserState();
                        }
                    }
                );
            }
        } else {
            const emptyField = !user.name
                ? 'Name'
                : !user.email
                ? 'Email'
                : !user.password
                ? 'Password'
                : 'Role';
            commonToast('error', `${emptyField} is required.`);
        }
    };

    const updateUser = (e) => {
        e.preventDefault();
        if (!user.name) {
            commonToast('error', 'Name is required.');
            return;
        }
        if (!user.role) {
            commonToast('error', 'Role is required.');
            return;
        }

        const bothPasswordsFilled = user.password && user.confirmPass;
        const bothPasswordsEmpty = !user.password && !user.confirmPass;
        if (!bothPasswordsFilled && !bothPasswordsEmpty) {
            commonToast('error', `${!user.password ? 'Password' : 'Confirm Password'} is required.`);
            return;
        }

        const data = { name: user.name, target: user.target, role: user.role, status: user.status };

        if (bothPasswordsFilled) {
            if (user.password !== user.confirmPass) {
                commonToast('error', 'Password and confirm password should be same.');
                return;
            }
            if (!validations(user.password, 'password')) return;
            data['password'] = user.password;
            data['confirmPassword'] = user.confirmPass;
        }

        setBtnLoading(true);
        hitApi({ url: 'user/update-user', method: 'PATCH', data }, (resp) => {
            setBtnLoading(false);
            if (resp.status) {
                handleClose();
                getUserList();
                emptyUserState();
            }
        });
    };

    // ─── Pagination helpers ───────────────────────────────────────────────────
    const cntStart = (state.page - 1) * state.limit + 1;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <div className="pxl_mainWrapper">
                <div className="pxl_rightBar_assets">

                    {/* ── Top header bar (breadcrumb / hamburger) ── */}
                    <div className="pxl-rb-header-wrap">
                        <div className="pxl-headingTop">
                            <h2 className="pxl-rb-header-heading">
                                {pageTitle.split(' · ')[0]}
                                {state.totalRecords ? <span className="pxl-rb-header-count"> ({state.totalRecords})</span> : null}
                            </h2>
                        </div>
                        <div className="pxl-rb-header-right">
                            <div
                                className="pxl-header-toogle-btn"
                                onClick={() =>
                                    store.updateStore('resMenuToggle', !store.resMenuToggle)
                                }
                            >
                                <svg
                                    className="pxl-header-toogle-svg"
                                    viewBox="0 0 100 100"
                                    width="80"
                                >
                                    <path
                                        className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-top"
                                        d="m 30,33 h 40 c 3.722839,0 7.5,3.126468 7.5,8.578427 0,5.451959 -2.727029,8.421573 -7.5,8.421573 h -20"
                                    />
                                    <path
                                        className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-middle"
                                        d="m 30,50 h 40"
                                    />
                                    <path
                                        className="pxl-header-toogle-svg-line pxl-header-toogle-svg-line-bottom"
                                        d="m 70,67 h -40 c 0,0 -7.5,-0.802118 -7.5,-8.365747 0,-7.563629 7.5,-8.634253 7.5,-8.634253 h 20"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* ── Page content ── */}
                    <div className="pxl-count-cards">
                        <div className="pxl-users-page">

                            {/* ── Toolbar ── */}
                            <div className="pxl-users-toolbar">
                                <div className="pxl-toolbar-right">

                                    {/* Account status filter */}
                                    <div className="pxl-select-wrap cun-headreSelect">
                                        <Select
                                            value={statusOption.filter(
                                                (opt) => opt.value === state.status
                                            )}
                                            defaultValue={null}
                                            options={statusOption}
                                            onChange={(e) =>
                                                setMyState(setState, {
                                                    status: e?.value ?? '',
                                                    page: 1,
                                                })
                                            }
                                            isClearable={true}
                                            placeholder="Account Status"
                                        />
                                    </div>

                                    {/* Verification status filter */}
                                    <div className="pxl-select-wrap cun-headreSelect">
                                        <Select
                                            value={verifiedOption.filter(
                                                (opt) => opt.value === state.verication_status
                                            )}
                                            defaultValue={null}
                                            options={verifiedOption}
                                            onChange={(e) =>
                                                setMyState(setState, {
                                                    verication_status: e?.value ?? '',
                                                    page: 1,
                                                })
                                            }
                                            isClearable={true}
                                            placeholder="Verification Status"
                                        />
                                    </div>

                                    {/* Search */}
                                    <div className="pxl-search-wrap">
                                        <input
                                            type="text"
                                            className="pxl-search-input"
                                            value={state.keyword}
                                            placeholder="Search Users"
                                            onChange={(e) =>
                                                setMyState(setState, {
                                                    keyword: e.target.value,
                                                    clearKeyword: false,
                                                })
                                            }
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.keyCode === 13 ||
                                                    e.which === 13
                                                ) {
                                                    getUserList();
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="pxl-search-btn"
                                            aria-label={state.keyword ? 'Clear search' : 'Search'}
                                            onClick={state.keyword ? clearSearch : undefined}
                                        >
                                            <img
                                                src="/assets/images/scheduler/search.svg"
                                                alt=""
                                            />
                                        </button>
                                    </div>

                                    {/* Add New User */}
                                    <button
                                        className="pxlCommon-btn pxl-add-user-btn"
                                        onClick={() => handleShow()}
                                    >
                                        <span className="pxl-btn-icon" aria-hidden="true">+</span>
                                        Add New User
                                    </button>

                                </div>
                            </div>

                            {/* ── Table ── */}
                            <div className="pxl-tbl-scroll">
                                <div className="pxl-users-table-wrap">
                                    <table className="pxl_table table-responsive support_table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>User</th>
                                                <th>Created At</th>
                                                <th>Last Login At</th>
                                                <th>Account Status</th>
                                                <th>Verification Status</th>
                                                <th>Source / Provider</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!state.userLoading ? (
                                                state.data.length > 0 ? (
                                                    state.data.map((item, index) => {
                                                        const rowNum =
                                                            state.page === 1
                                                                ? index + 1
                                                                : index + 1 + (state.page - 1) * state.limit;

                                                        const isActive = item.status === 1;
                                                        const isVerified = !item?.verificationToken;

                                                        return (
                                                            <tr key={item._id || index}>
                                                                <td>
                                                                    <p>{rowNum}</p>
                                                                </td>
                                                                <td>
                                                                    <div className="pxl-user-cell">
                                                                        <div className="pxl-user-avatar">
                                                                            {item.profilePic
                                                                                ? <img src={assetsUrlPath(item.profilePic)} alt={item.name} />
                                                                                : <span>{getNameInitials(item.name)}</span>}
                                                                        </div>
                                                                        <div className="pxl-user-info">
                                                                            <p className="pxl-user-name">{item.name}</p>
                                                                            <p className="pxl-user-email">{item.email}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <p>{dateFormatter(item.createdAt, 'MMM Do, YY')}</p>
                                                                </td>
                                                                <td>
                                                                    <p>
                                                                        {item.lastLogin
                                                                            ? dateFormatter(item.lastLogin, 'MMM Do, YY - hh:mm A')
                                                                            : '—'}
                                                                    </p>
                                                                </td>

                                                                {/* Account status badge */}
                                                                <td>
                                                                    <span
                                                                        className={`pxl-badge ${
                                                                            isActive
                                                                                ? 'pxl-badge--active'
                                                                                : 'pxl-badge--inactive'
                                                                        }`}
                                                                    >
                                                                        {isActive ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>

                                                                {/* Verification status badge */}
                                                                <td>
                                                                    <span
                                                                        className={`pxl-badge ${
                                                                            isVerified
                                                                                ? 'pxl-badge--verified'
                                                                                : 'pxl-badge--unverified'
                                                                        }`}
                                                                    >
                                                                        {isVerified ? 'Verified' : 'Not Verified'}
                                                                    </span>
                                                                </td>

                                                                {/* Source / provider badges */}
                                                                <td>
                                                                    <div className="pxl-source-cell">
                                                                        <span className="pxl-badge pxl-badge--source">
                                                                            {item?.source || '—'}
                                                                        </span>
                                                                        {item.provider && (
                                                                            <span className="pxl-badge pxl-badge--source">
                                                                                {item.provider}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* Actions */}
                                                                <td>
                                                                    <div className="pxl-tbl-action-btn-group">
                                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                                                                            <a
                                                                                href="#"
                                                                                onClick={(e) => { e.preventDefault(); handleShow(item); }}
                                                                                className="pxl-tbl-action-btn"
                                                                            >
                                                                                {svg.app.editdefault}
                                                                            </a>
                                                                        </OverlayTrigger>
                                                                        <OverlayTrigger placement="top" overlay={<Tooltip>View</Tooltip>}>
                                                                            <Link
                                                                                to={`/admin/user-details/${item._id}`}
                                                                                className="pxl-tbl-action-btn"
                                                                                target="_blank"
                                                                            >
                                                                                {svg.app.table_view_icon}
                                                                            </Link>
                                                                        </OverlayTrigger>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : state.keyword ? (
                                                    <NoDataFoundDataTable
                                                        column={8}
                                                        statement="You don't have user with this name."
                                                    />
                                                ) : (
                                                    <NoDataFoundDataTable
                                                        column={8}
                                                        statement="No user found."
                                                    />
                                                )
                                            ) : (
                                                <tr>
                                                    <td colSpan={8}>{skeletonLoader('table')}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* ── Pagination ── */}
                            <div className="pxl-pagination-wrap">
                                <Pagination
                                    type="User"
                                    dataRange={{
                                        start: state.totalRecords ? cntStart : 0,
                                        end: state.totalRecords
                                            ? cntStart + state.data.length - 1
                                            : 0,
                                    }}
                                    showPagination={true}
                                    currentPage={state.page}
                                    totalRecords={state.totalRecords}
                                    perPage={state.limit}
                                    isLoading={state.userLoading}
                                    onClick={(pageNum) =>
                                        setMyState(setState, { page: pageNum })
                                    }
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* ── Add / Edit User Modal (unchanged markup) ── */}
            <Modal
                show={showModal}
                onHide={handleClose}
                animation={false}
                size="lg"
                aria-labelledby="users-modal-title"
                centered
                className="pxl_modalDv"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="users-modal-title">
                        {user.isEdit ? 'Edit User' : 'Add New User'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form action="" onSubmit={user.isEdit ? updateUser : createUser}>

                        <div className="form-group">
                            <label htmlFor="userName">
                                User Name <span className="pxl-star-red">*</span>
                            </label>
                            <input
                                ref={firstInputRef}
                                id="userName"
                                type="text"
                                className="form-control"
                                value={user.name}
                                placeholder="Enter Name"
                                onChange={(e) =>
                                    setMyState(setUser, { name: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="userEmail">
                                Email Address <span className="pxl-star-red">*</span>
                            </label>
                            <input
                                id="userEmail"
                                type="email"
                                className="form-control"
                                value={user.email}
                                disabled={user.isEdit}
                                placeholder="Enter Email"
                                onChange={(e) =>
                                    setMyState(setUser, { email: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="userPassword">
                                Password{' '}
                                {!user.isEdit && <span className="pxl-star-red">*</span>}
                            </label>
                            <input
                                id="userPassword"
                                type="password"
                                className="form-control"
                                value={user.password}
                                placeholder="Enter Password"
                                onChange={(e) =>
                                    setMyState(setUser, { password: e.target.value })
                                }
                            />
                        </div>

                        {user.isEdit && (
                            <div className="form-group">
                                <label htmlFor="userConfirmPass">Confirm Password</label>
                                <input
                                    id="userConfirmPass"
                                    type="password"
                                    className="form-control"
                                    value={user.confirmPass}
                                    placeholder="Enter Confirm Password"
                                    onChange={(e) =>
                                        setMyState(setUser, { confirmPass: e.target.value })
                                    }
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="userRole">
                                Role <span className="pxl-star-red">*</span>
                            </label>
                            <div className="cun-headreSelect p-0">
                                <Select
                                    inputId="userRole"
                                    options={optionsAdmin}
                                    value={optionsAdmin.filter(
                                        (opt) => opt.value === user.role
                                    )}
                                    onChange={(e) =>
                                        setMyState(setUser, { role: e.value })
                                    }
                                />
                            </div>
                        </div>

                        <div className="sch-statusCheckbox sch-relativeClass">
                            {user.isEdit && (
                                <div className="sch-custom-check-box">
                                    <div className="form-group">
                                        <input
                                            type="checkbox"
                                            checked={!!user.status}
                                            className="form-check-input"
                                            id="userStatus"
                                            onChange={(e) =>
                                                setMyState(setUser, { status: e.target.checked })
                                            }
                                        />
                                        <label className="form-check-label" htmlFor="userStatus">
                                            Status
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button type="submit" className="pxl_btn">
                            {btnLoading
                                ? 'Processing...'
                                : user.isEdit
                                ? 'Update'
                                : 'Submit'}
                        </button>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default Users;
